/** @jsxImportSource @emotion/react */
"use client";

import type {
  PostPrayer as PostPrayerShape,
  PostPrayerOnReflect,
} from "@packages/media";
import { PostPrayerContext, usePostPrayer } from "@packages/media";
import type { PostPrayerStep } from "@packages/sdk";
import {
  COOKIE_KEY_DOWNLOAD_MODAL_COUNT,
  useCookie,
  useRequestMe,
  useRequestPostPrayer,
} from "@packages/sdk";
import { useIsDesktopViewport, useModal } from "@packages/ui";
import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import isEqual from "react-fast-compare";

import { PostPrayerModal } from "../blocks/groups/PostPrayerModal/PostPrayerModal.emotion";

const UNSUPPORTED_STEPS: PostPrayerStep["type"][] = [
  "intentions",
  "rating",
  "signup",
  "recommendation",
  "celebration_activity",
];

const PostPrayer = () => {
  const modal = useModal(PostPrayerModal);
  const { steps, clearSteps, onReflect } = usePostPrayer();

  useEffect(() => {
    if (steps.length) {
      modal.show({ steps, onReflect }).then(() => clearSteps());
    }

    return () => {
      modal.hide();
    };
  }, [steps, modal]);

  return <></>;
};

export const PostPrayerProvider = ({ children }: PropsWithChildren) => {
  const { query: getPostPrayer } = useRequestPostPrayer();
  const { query: me } = useRequestMe();
  const remainingSteps = useRef<PostPrayerStep[]>([]);
  const [modalSteps, setModalSteps] = useState<PostPrayerStep[]>([]);
  const [downloadModalCount, setDownloadModalCount] = useCookie({
    defaultValue: 0,
    key: COOKIE_KEY_DOWNLOAD_MODAL_COUNT,
  });
  const isDesktop = useIsDesktopViewport();
  const [onReflect, setOnReflect] = useState<PostPrayerOnReflect | null>(null);

  const trigger: PostPrayerShape["trigger"] = useCallback(
    async (type) => {
      const postPrayerSteps = await getPostPrayer.mutateAsync({ type });

      const newSteps = [...modalSteps];

      for (const step of postPrayerSteps.steps) {
        if (
          !UNSUPPORTED_STEPS.includes(step.type) &&
          !(
            step.type === "default" &&
            (step.deeplink?.includes("routine") ||
              step.secondary_deeplink?.includes("routine"))
          ) &&
          !newSteps.find((existing) => existing.id === step.id)
        ) {
          newSteps.push(step);
          continue;
        }
        if (
          remainingSteps.current.find((existing) => isEqual(step, existing))
        ) {
          continue;
        }
        remainingSteps.current.push(step);
      }

      if (
        !me.data?.has_downloaded_app &&
        (!isDesktop || (isDesktop && downloadModalCount === 0)) &&
        !newSteps.find((existing) => existing.id === -1)
      ) {
        setDownloadModalCount(downloadModalCount + 1);
        // Add download step to the beginning of the steps array
        newSteps.unshift({
          type: "download",
          id: -1,
        });
      }

      setModalSteps(newSteps);
    },
    [modalSteps, me.data, isDesktop, downloadModalCount],
  );

  const clearSteps = () => setModalSteps([]);

  return (
    <PostPrayerContext.Provider
      value={{
        trigger,
        steps: modalSteps,
        clearSteps,
        onReflect,
        setOnReflect,
      }}
    >
      {children}
      <PostPrayer />
    </PostPrayerContext.Provider>
  );
};
